import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthFrame } from '@/components/AuthFrame';
import { authClient } from '@/lib/auth-client';
import { oauthResumeUrl } from '@/lib/oauth';
import { meQuery, safeRedirect } from '@/lib/session';

export const Route = createFileRoute('/login')({
  // Loose: an OAuth authorize request (Claude Code) arrives with its signed parameters, kept as-is.
  validateSearch: z.looseObject({ redirect: z.string().optional(), expired: z.boolean().optional() }),
  component: LoginPage
});

function LoginPage() {
  const { t } = useTranslation();
  const { redirect, expired } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const forOAuth = oauthResumeUrl(window.location.search) !== null;

  const done = async () => {
    // Signed in for Claude Code (OAuth): resume the authorization request; with a session it
    // continues to the consent page. Otherwise the usual redirect inside the app.
    const resume = oauthResumeUrl(window.location.search);
    if (resume) return window.location.assign(resume);
    await queryClient.invalidateQueries({ queryKey: meQuery().queryKey });
    await navigate({ to: safeRedirect(redirect) as never, replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await authClient.signIn.email({ email, password });
      if (err) return setError(err.status === 429 ? t('auth.tooManyAttempts') : t('auth.invalidCredentials'));
      await done();
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    const res = await authClient.signIn.passkey();
    if (res?.error) return setError(t('auth.passkeyFailed'));
    await done();
  };

  return (
    <AuthFrame title={t('auth.signInTitle')} lead={forOAuth ? t('auth.forOAuth') : t('auth.signInLead')}>
      {expired && <div className="alert alert-info alert-soft mb-4 text-sm">{t('auth.sessionExpired')}</div>}
      <form onSubmit={submit} className="space-y-3">
        <label className="form-control block">
          <span className="label-text mb-1 block text-sm">{t('auth.email')}</span>
          <input className="input w-full" type="email" autoComplete="username webauthn" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
        </label>
        <label className="form-control block">
          <span className="label-text mb-1 block text-sm">{t('auth.password')}</span>
          <input className="input w-full" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        <div className="text-end">
          <Link className="text-base-content/70 text-xs hover:underline" to="/forgot-password">
            {t('auth.forgotPassword')}
          </Link>
        </div>
        {error && (
          <p className="text-error text-sm" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy && <span className="loading loading-spinner loading-sm" />}
          {t('auth.signIn')}
        </button>
      </form>
      <div className="divider text-base-content/60 text-xs">{t('auth.or')}</div>
      <button type="button" className="btn btn-outline w-full" onClick={() => void passkey()}>
        {t('auth.passkey')}
      </button>
    </AuthFrame>
  );
}
